/**
 * The ⌘K palette.
 *
 * Shared Docs has rendered a `⌘K` hint beside its search field since before
 * anything implemented it -- that hint is the contract this closes. What it
 * searches is decided by `quickOpenSources`; how results are ordered is decided
 * by `rankQuickOpenEntries` in the shared bundle, which is the same match-and-
 * rank the desktop's quick open uses for an issue key.
 *
 * Keyboard contract, matching the desktop dialog: ArrowUp/ArrowDown move the
 * active row, Enter opens it, Escape closes the palette. The active row is
 * state rather than DOM focus, so the caret never leaves the query field --
 * typing after arrowing has to keep refining the query, not start over.
 *
 * Positioned and dismissed through `@floating-ui/react`. It is centred on the
 * viewport rather than anchored to anything, so `FloatingOverlay` supplies the
 * scrim and the scroll lock and no coordinate is computed here.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { rankQuickOpenEntries } from '@nimbalyst/collab-bundle/quick-open';
import { DecorativeIcon } from '../organization/DecorativeIcon';
import type { QuickOpenTarget } from './quickOpenSources';

export interface QuickOpenPaletteProps {
  open: boolean;
  onClose: () => void;
  /** Read once per open; the palette is transient and does not live-update. */
  entries: readonly QuickOpenTarget[];
  onSelect: (target: QuickOpenTarget) => void;
  /**
   * Shown under the results when Shared Docs has never been opened in this page
   * load, so an empty document half reads as "not loaded" rather than "none".
   */
  documentsUnavailable?: boolean;
}

const KIND_LABEL: Record<QuickOpenTarget['kind'], string> = {
  document: 'Document',
  tracker: 'Tracker item',
};

export function QuickOpenPalette({
  open,
  onClose,
  entries,
  onSelect,
  documentsUnavailable = false,
}: QuickOpenPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const { refs, context } = useFloating({
    open,
    onOpenChange: (nextOpen) => { if (!nextOpen) onClose(); },
  });
  const { getFloatingProps } = useInteractions([
    useDismiss(context, { outsidePressEvent: 'mousedown' }),
    useRole(context, { role: 'dialog' }),
  ]);

  const results = useMemo(
    () => rankQuickOpenEntries(entries, query, { limit: 60 }),
    [entries, query],
  );

  // A fresh palette starts empty rather than showing the last search.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [open]);

  // The ranking changes under the cursor on every keystroke, so the active row
  // moves back to the top rather than pointing at whatever landed at its index.
  useEffect(() => { setActiveIndex(0); }, [query]);

  useEffect(() => {
    const row = listRef.current?.querySelectorAll('.quick-open-row')[activeIndex];
    // Called through an optional member: jsdom's HTMLElement has no
    // `scrollIntoView` at all, and a keyboard test has no business needing a
    // stub for a scroll the assertion does not look at.
    (row as HTMLElement | undefined)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, results]);

  const choose = useCallback((target: QuickOpenTarget | undefined) => {
    if (!target) return;
    onSelect(target);
    onClose();
  }, [onClose, onSelect]);

  const onKeyDown = useCallback((event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((index) => (results.length === 0 ? 0 : (index + 1) % results.length));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((index) => (
          results.length === 0 ? 0 : (index - 1 + results.length) % results.length
        ));
        break;
      case 'Enter':
        event.preventDefault();
        choose(results[activeIndex]);
        break;
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
      default:
        break;
    }
  }, [activeIndex, choose, onClose, results]);

  if (!open) return null;

  return (
    <FloatingPortal>
      <FloatingOverlay
        className="quick-open-overlay z-[200] grid place-items-start justify-center bg-[color-mix(in_srgb,black_45%,transparent)] pt-[12vh]"
        lockScroll
      >
        <FloatingFocusManager context={context} modal initialFocus={0}>
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            className="quick-open-palette flex max-h-[min(32rem,70vh)] w-[min(40rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-[0.9rem] border border-nim bg-nim shadow-[var(--console-shadow-lg)]"
            aria-label="Quick open"
            onKeyDown={onKeyDown}
          >
            <div className="quick-open-field flex shrink-0 items-center gap-2 border-b border-nim px-3.5 py-3">
              <DecorativeIcon icon="search" size={18} className="text-nim-faint" />
              <input
                className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[0.95rem] text-nim outline-none placeholder:text-nim-faint"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search documents and tracker items"
                aria-label="Search documents and tracker items"
                // The list is state, not focus, so the field must say which row
                // Enter would open.
                role="combobox"
                aria-expanded
                aria-controls="quick-open-results"
                aria-activedescendant={results[activeIndex] ? rowId(results[activeIndex]!) : undefined}
                autoComplete="off"
                spellCheck={false}
              />
              <kbd className="shrink-0 rounded border border-nim px-1 py-0.5 text-[10.5px] text-nim-faint">esc</kbd>
            </div>

            {results.length === 0 ? (
              <div className="quick-open-empty px-3.5 py-8 text-center text-[0.82rem] text-nim-muted">
                {query.trim() ? 'Nothing matches that.' : 'Nothing here to open yet.'}
              </div>
            ) : (
              <ul
                ref={listRef}
                id="quick-open-results"
                role="listbox"
                className="quick-open-results min-h-0 flex-1 overflow-y-auto py-1"
              >
                {results.map((target, index) => (
                  <li key={rowId(target)} role="none">
                    <button
                      type="button"
                      id={rowId(target)}
                      role="option"
                      aria-selected={index === activeIndex}
                      data-quick-open-kind={target.kind}
                      tabIndex={-1}
                      className={`quick-open-row flex w-full cursor-pointer items-center gap-2.5 border-none px-3.5 py-2 text-left ${
                        index === activeIndex ? 'bg-nim-selected' : 'bg-transparent hover:bg-nim-hover'
                      }`}
                      // `mousedown` rather than `click`: the overlay dismisses
                      // on mousedown, so a click handler would never run.
                      onMouseDown={(event) => {
                        event.preventDefault();
                        choose(target);
                      }}
                      onMouseEnter={() => setActiveIndex(index)}
                    >
                      <DecorativeIcon
                        icon={target.icon ?? (target.kind === 'tracker' ? 'checklist' : 'description')}
                        size={17}
                        className="shrink-0 text-nim-faint"
                      />
                      {target.key ? (
                        <span className="shrink-0 font-mono text-[11px] uppercase tracking-[0.06em] text-nim-muted">
                          {target.key}
                        </span>
                      ) : null}
                      <span className="min-w-0 flex-1 truncate text-[0.85rem] text-nim">{target.title}</span>
                      {target.subtitle ? (
                        // No responsive variant: this app's Tailwind pass has no
                        // container-query plugin, and a `@media` breakpoint is
                        // the wrong unit for a palette that is already clamped
                        // to the viewport. It shrinks and truncates instead.
                        <span className="min-w-0 max-w-[16rem] shrink truncate text-[0.7rem] text-nim-faint">
                          {target.subtitle}
                        </span>
                      ) : null}
                      <span className="shrink-0 text-[0.62rem] uppercase tracking-[0.08em] text-nim-faint">
                        {KIND_LABEL[target.kind]}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="quick-open-footer flex shrink-0 items-center gap-3 border-t border-nim px-3.5 py-2 text-[0.66rem] text-nim-faint">
              <span><kbd className="font-mono">↑↓</kbd> move</span>
              <span><kbd className="font-mono">↵</kbd> open</span>
              <span><kbd className="font-mono">esc</kbd> close</span>
              {documentsUnavailable && (
                <span className="ml-auto text-right">Open Shared Docs once to search documents too.</span>
              )}
            </div>
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  );
}

/** Unique across corpora: a document id and an item id can collide. */
function rowId(target: QuickOpenTarget): string {
  return `quick-open-${target.kind}-${target.id}`;
}
